import {
  Children as reactChildren,
  cloneElement,
  createContext,
  ReactElement,
  ReactNode,
  useContext,
} from 'react';
import {
  ColorProps,
  ListItem,
  ListItemProps,
  ListProps,
  Text,
  TextProps,
  UnorderedList,
  useMultiStyleConfig,
} from '@chakra-ui/react';

export interface TimelineProps extends ListProps {
  reverse?: boolean;
  mode?: 'left' | 'alternate' | 'right';
}
export interface TimelineItemProps {
  status?: string;
  index?: number;
  dot?: ReactNode;
  label?: ReactNode;
  children?: ReactNode;
  containerProps?: ListItemProps;
  labelProps?: TextProps;
  contentProps?: TextProps;
  connectorProps?: TextProps;
  dotProps?: TextProps;
}

const TimelineContext = createContext<{
  mode: TimelineProps['mode'];
  totalItems: number;
}>({
  mode: 'right',
  totalItems: 0,
});
const TimelineContextProvider = TimelineContext.Provider;

const TimelineItem = (props: TimelineItemProps) => {
  const { mode, totalItems } = useContext(TimelineContext);
  const styles = useMultiStyleConfig(`Timeline`, {});
  const {
    dot,
    label,
    children,
    index = 0,
    status = 'inprogress',
    containerProps,
    connectorProps,
    contentProps,
    dotProps,
    labelProps,
  } = props;

  let color: ColorProps['color'] = 'gray.500';
  switch (status) {
    case 'success':
      color = 'green.500';
      break;
    case 'error':
      color = 'red.500';
      break;
    case 'waiting':
      color = 'orange.500';
      break;
  }

  let dotStyles = {},
    connectorStyles = {},
    textStyles = {};
  switch (mode) {
    case 'right':
      dotStyles = { right: 0 };
      connectorStyles = { right: 1 };
      break;
    case 'alternate':
      dotStyles = { marginLeft: -1, left: '50%' };
      connectorStyles = { left: '50%' };
      textStyles =
        index % 2 !== 0
          ? {
              left: 'calc(50% + 14px)',
              width: 'calc(50% - 14px)',
              textAlign: 'left',
            }
          : { width: 'calc(50% - 12px)', textAlign: 'right' };
      break;
    default:
      connectorStyles = { left: 1 };
      textStyles = { left: 7 };
      break;
  }

  return (
    <ListItem sx={{ ...styles.TimelineItem }} {...containerProps}>
      {label && (
        <Text sx={{ ...styles.Label }} {...textStyles} {...labelProps}>
          {label}
        </Text>
      )}
      <Text
        color={color}
        display={index === totalItems - 1 ? 'none' : ''}
        sx={{ ...styles.Connector }}
        {...connectorStyles}
        {...connectorProps}
      />
      <Text
        color={color}
        sx={dot ? {} : { ...styles.Dot }}
        {...dotStyles}
        {...dotProps}
      >
        {dot}
      </Text>
      <Text sx={{ ...styles.Content }} {...textStyles} {...contentProps}>
        {children}
      </Text>
    </ListItem>
  );
};

export const Timeline = (props: TimelineProps) => {
  const { reverse = false, mode = 'left', children, ...rest } = props;
  const styles = useMultiStyleConfig(`Timeline`, {});

  const timelineItems = reactChildren.toArray(children);
  if (reverse) {
    timelineItems.reverse();
  }

  return (
    <TimelineContextProvider
      value={{ mode, totalItems: reactChildren.count(timelineItems) }}
    >
      <UnorderedList sx={{ ...styles.Timeline }} {...rest}>
        {reactChildren.map(timelineItems, (item, index) => {
          return cloneElement(item as ReactElement, { index: index });
        })}
      </UnorderedList>
    </TimelineContextProvider>
  );
};

Timeline.Item = TimelineItem;
